import { useEffect, useMemo, useRef, useState } from 'react'
import type { BlissBasicGalleryPhotoRow } from '../../../services/blissBasicCatalogAdminService'
import {
  photoPublicUrl,
  uploadBlissBasicGalleryPhoto,
} from '../../../services/blissBasicCatalogAdminService'
import {
  deleteBlissBasicOptionItem,
  fetchBlissBasicOptionItems,
  reorderBlissBasicOptionItemsForStep,
  seedBlissBasicOptionItemsFromDefaults,
  slugifyChoiceKey,
  upsertBlissBasicOptionItem,
  type BlissBasicOptionItemRow,
} from '../../../services/blissBasicOptionItemsAdminService'
import {
  BLISS_BASIC_CONFIGURATOR_STEP_IDS,
  defaultConfiguratorStepDefs,
} from '../../../utils/blissBasicConfiguratorDefaults'
import type { BlissBasicConfiguratorStepCopy } from '../../../config/blissBasicCatalog'

type Props = {
  tenantId: string
  stepCopy: BlissBasicConfiguratorStepCopy
  onSaveStepCopy: (copy: BlissBasicConfiguratorStepCopy) => Promise<void>
  photos: BlissBasicGalleryPhotoRow[]
  onReload: () => Promise<void>
}

export default function BlissBasicConfiguratorAdminSection({
  tenantId,
  stepCopy,
  onSaveStepCopy,
  photos,
  onReload,
}: Props) {
  const [items, setItems] = useState<BlissBasicOptionItemRow[]>([])
  const [loading, setLoading] = useState(true)
  const [editItem, setEditItem] = useState<BlissBasicOptionItemRow | 'new' | null>(null)
  const [newStepId, setNewStepId] = useState<string>('layout')
  const [copyForm, setCopyForm] = useState(stepCopy)
  useEffect(() => {
    setCopyForm(stepCopy)
  }, [stepCopy])
  const [seeding, setSeeding] = useState(false)
  const [savingCopy, setSavingCopy] = useState(false)
  const [copySaveMessage, setCopySaveMessage] = useState<string | null>(null)
  const [reorderingStepId, setReorderingStepId] = useState<string | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [pendingPhotoItemId, setPendingPhotoItemId] = useState<string | null>(null)

  const stepDefs = useMemo(() => defaultConfiguratorStepDefs(), [])

  const loadItems = async () => {
    setLoading(true)
    const rows = await fetchBlissBasicOptionItems(tenantId)
    setItems(rows)
    setLoading(false)
  }

  useEffect(() => {
    void loadItems()
  }, [tenantId])

  const itemsForStep = (stepId: string) =>
    items.filter((i) => i.step_id === stepId).sort((a, b) => a.sort_order - b.sort_order)

  const photoForItem = (row: BlissBasicOptionItemRow) =>
    photos.find((p) => p.id === row.gallery_photo_id)

  const saveCopy = async () => {
    setSavingCopy(true)
    setCopySaveMessage(null)
    try {
      const fullCopy: BlissBasicConfiguratorStepCopy = {}
      for (const stepId of BLISS_BASIC_CONFIGURATOR_STEP_IDS) {
        const def = stepDefs.find((s) => s.id === stepId)!
        fullCopy[stepId] = {
          title: copyForm[stepId]?.title?.trim() || def.title,
          subtitle: copyForm[stepId]?.subtitle?.trim() || def.subtitle,
        }
      }
      await onSaveStepCopy(fullCopy)
      setCopyForm(fullCopy)
      setCopySaveMessage('Saved — customers will see this copy on the options page.')
    } catch {
      setCopySaveMessage('Could not save step copy. Check your connection and try again.')
    } finally {
      setSavingCopy(false)
    }
  }

  const moveChoice = async (stepId: string, rowId: string, direction: -1 | 1) => {
    const rows = itemsForStep(stepId)
    const index = rows.findIndex((r) => r.id === rowId)
    const swapIndex = index + direction
    if (index < 0 || swapIndex < 0 || swapIndex >= rows.length) return
    const reordered = [...rows]
    ;[reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]]
    setReorderingStepId(stepId)
    try {
      await reorderBlissBasicOptionItemsForStep(
        tenantId,
        stepId,
        reordered.map((r) => r.id),
      )
      await loadItems()
      await onReload()
    } finally {
      setReorderingStepId(null)
    }
  }

  const seedDefaults = async () => {
    setSeeding(true)
    await seedBlissBasicOptionItemsFromDefaults(tenantId)
    await loadItems()
    setSeeding(false)
  }

  const saveItem = async () => {
    if (!editItem) return
    if (editItem === 'new') {
      const label = (document.getElementById('bboi-new-label') as HTMLInputElement)?.value?.trim()
      if (!label) return
      const key =
        (document.getElementById('bboi-new-key') as HTMLInputElement)?.value?.trim() ||
        slugifyChoiceKey(label)
      await upsertBlissBasicOptionItem(tenantId, {
        step_id: newStepId,
        choice_key: key,
        label,
        detail: (document.getElementById('bboi-new-detail') as HTMLTextAreaElement)?.value?.trim() || null,
        price_usd: Number((document.getElementById('bboi-new-price') as HTMLInputElement)?.value) || null,
        sort_order: itemsForStep(newStepId).length,
      })
    } else {
      await upsertBlissBasicOptionItem(tenantId, editItem)
    }
    setEditItem(null)
    await loadItems()
    await onReload()
  }

  const attachPhotoUpload = async (
    file: File,
    item: BlissBasicOptionItemRow,
  ): Promise<BlissBasicOptionItemRow | null> => {
    const photo = await uploadBlissBasicGalleryPhoto(tenantId, file, {
      title: item.label,
      description: item.detail ?? undefined,
      price_usd: item.price_usd,
    })
    const saved = await upsertBlissBasicOptionItem(tenantId, {
      ...item,
      gallery_photo_id: photo.id,
    })
    await loadItems()
    await onReload()
    return saved
  }

  return (
    <section className="bbp-section">
      <h3>Options selection wizard</h3>
      <p className="bbp-lede">
        Edit every choice customers see on the options page — labels, pricing, and photos. Tap{' '}
        <strong>+</strong> on the site to expand details.
      </p>

      <div className="bbp-actions" style={{ marginBottom: 16 }}>
        <button type="button" className="bbp-btn bbp-btn-primary" disabled={seeding} onClick={() => void seedDefaults()}>
          {seeding ? 'Importing…' : 'Import factory defaults'}
        </button>
        <button type="button" className="bbp-btn" onClick={() => void loadItems()}>
          Refresh
        </button>
      </div>

      <h4>Step titles (customer-facing)</h4>
      <div className="bbp-grid-2">
        {BLISS_BASIC_CONFIGURATOR_STEP_IDS.map((stepId) => {
          const def = stepDefs.find((s) => s.id === stepId)!
          return (
            <div key={stepId} className="bbp-field">
              <label>{def.title} — headline</label>
              <input
                value={copyForm[stepId]?.title ?? def.title}
                onChange={(e) =>
                  setCopyForm((c) => ({
                    ...c,
                    [stepId]: { ...c[stepId], title: e.target.value },
                  }))
                }
              />
              <label style={{ marginTop: 8 }}>Subline</label>
              <input
                value={copyForm[stepId]?.subtitle ?? def.subtitle}
                onChange={(e) =>
                  setCopyForm((c) => ({
                    ...c,
                    [stepId]: { ...c[stepId], subtitle: e.target.value },
                  }))
                }
              />
            </div>
          )
        })}
      </div>
      <div className="bbp-actions">
        <button
          type="button"
          className="bbp-btn bbp-btn-primary"
          disabled={savingCopy}
          onClick={() => void saveCopy()}
        >
          {savingCopy ? 'Saving…' : 'Save step copy'}
        </button>
        {copySaveMessage && (
          <p className="bbp-photo-hint" style={{ margin: 0 }} role="status">
            {copySaveMessage}
          </p>
        )}
      </div>

      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          const itemId = pendingPhotoItemId
          const item = items.find((i) => i.id === itemId)
          if (file && item) {
            void attachPhotoUpload(file, item).then((saved) => {
              if (saved && editItem && editItem !== 'new' && editItem.id === saved.id) {
                setEditItem(saved)
              }
            })
          }
          setPendingPhotoItemId(null)
          if (photoInputRef.current) photoInputRef.current.value = ''
        }}
      />

      {loading && <p>Loading options…</p>}

      {BLISS_BASIC_CONFIGURATOR_STEP_IDS.map((stepId) => {
        const def = stepDefs.find((s) => s.id === stepId)!
        const rows = itemsForStep(stepId)
        return (
          <div key={stepId} className="bbp-cat-block">
            <h4>
              {def.title}
              <span className="bbp-cat-examples">{rows.length} choices</span>
            </h4>
            {rows.length === 0 ? (
              <p className="bbp-empty-cat">No rows yet — import defaults or add a choice.</p>
            ) : (
              <div className="bbp-table-scroll">
                <table className="bbp-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Label</th>
                      <th>Price</th>
                      <th>Photo</th>
                      <th>Stock</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => {
                      const ph = photoForItem(row)
                      const reorderBusy = reorderingStepId === stepId
                      return (
                        <tr key={row.id}>
                          <td className="bbp-reorder-cell">
                            <button
                              type="button"
                              className="bbp-btn bbp-btn-small"
                              disabled={reorderBusy || rowIndex === 0}
                              aria-label={`Move ${row.label} up`}
                              onClick={() => void moveChoice(stepId, row.id, -1)}
                            >
                              ↑
                            </button>{' '}
                            <button
                              type="button"
                              className="bbp-btn bbp-btn-small"
                              disabled={reorderBusy || rowIndex === rows.length - 1}
                              aria-label={`Move ${row.label} down`}
                              onClick={() => void moveChoice(stepId, row.id, 1)}
                            >
                              ↓
                            </button>
                          </td>
                          <td>
                            <strong>{row.label}</strong>
                            <div className="bbp-cat-examples">{row.detail}</div>
                          </td>
                          <td>
                            {row.price_usd != null
                              ? `${row.price_label ?? 'From'} $${Number(row.price_usd).toLocaleString()}`
                              : '—'}
                          </td>
                          <td>
                            {ph ? (
                              <img
                                src={photoPublicUrl(ph.storage_path)}
                                alt=""
                                style={{ width: 56, height: 40, objectFit: 'cover' }}
                              />
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>{row.in_stock ? 'In stock' : 'Hidden'}</td>
                          <td>
                            <button type="button" className="bbp-btn bbp-btn-small" onClick={() => setEditItem({ ...row })}>
                              Edit
                            </button>{' '}
                            <button
                              type="button"
                              className="bbp-btn bbp-btn-small"
                              onClick={() => {
                                setPendingPhotoItemId(row.id)
                                photoInputRef.current?.click()
                              }}
                            >
                              Photo
                            </button>{' '}
                            <button
                              type="button"
                              className="bbp-btn bbp-btn-danger bbp-btn-small"
                              onClick={() => {
                                if (confirm('Delete this choice?')) {
                                  void deleteBlissBasicOptionItem(tenantId, row.id).then(loadItems)
                                }
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <button
              type="button"
              className="bbp-btn"
              onClick={() => {
                setNewStepId(stepId)
                setEditItem('new')
              }}
            >
              Add choice to {def.title}
            </button>
          </div>
        )
      })}

      {editItem && (
        <div className="bbp-modal-backdrop" role="presentation" onClick={() => setEditItem(null)}>
          <div className="bbp-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{editItem === 'new' ? 'New choice' : 'Edit choice'}</h3>
            {editItem === 'new' ? (
              <>
                <div className="bbp-field">
                  <label htmlFor="bboi-new-label">Label</label>
                  <input id="bboi-new-label" />
                </div>
                <div className="bbp-field">
                  <label htmlFor="bboi-new-key">Key (optional slug)</label>
                  <input id="bboi-new-key" placeholder="auto from label" />
                </div>
                <div className="bbp-field">
                  <label htmlFor="bboi-new-detail">Detail text</label>
                  <textarea id="bboi-new-detail" rows={3} />
                </div>
                <div className="bbp-field">
                  <label htmlFor="bboi-new-price">Price USD (optional)</label>
                  <input id="bboi-new-price" type="number" min={0} step={100} />
                </div>
              </>
            ) : (
              <>
                <div className="bbp-field">
                  <label>Label</label>
                  <input
                    value={editItem.label}
                    onChange={(e) => setEditItem({ ...editItem, label: e.target.value })}
                  />
                </div>
                <div className="bbp-field">
                  <label>Detail</label>
                  <textarea
                    rows={3}
                    value={editItem.detail ?? ''}
                    onChange={(e) => setEditItem({ ...editItem, detail: e.target.value || null })}
                  />
                </div>
                <div className="bbp-field">
                  <label>Price label</label>
                  <input
                    value={editItem.price_label ?? ''}
                    onChange={(e) => setEditItem({ ...editItem, price_label: e.target.value || null })}
                  />
                </div>
                <div className="bbp-field">
                  <label>Price USD</label>
                  <input
                    type="number"
                    value={editItem.price_usd ?? ''}
                    onChange={(e) =>
                      setEditItem({
                        ...editItem,
                        price_usd: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="bbp-field">
                  <label>Price high USD</label>
                  <input
                    type="number"
                    value={editItem.price_high_usd ?? ''}
                    onChange={(e) =>
                      setEditItem({
                        ...editItem,
                        price_high_usd: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="bbp-field bbp-field--photo">
                  <label>Photo for this choice</label>
                  <p className="bbp-photo-hint">
                    Shoppers see this image when they expand or select this option on the landing wizard.
                  </p>
                  {(() => {
                    const ph = photoForItem(editItem)
                    return ph ? (
                      <img
                        className="bbp-modal-photo-preview"
                        src={photoPublicUrl(ph.storage_path)}
                        alt={editItem.label}
                      />
                    ) : (
                      <p className="bbp-photo-hint">No photo yet.</p>
                    )
                  })()}
                  <button
                    type="button"
                    className="bbp-btn bbp-btn-primary"
                    onClick={() => {
                      setPendingPhotoItemId(editItem.id)
                      photoInputRef.current?.click()
                    }}
                  >
                    {photoForItem(editItem) ? 'Replace photo' : 'Upload photo'}
                  </button>
                </div>

                <div className="bbp-field bbp-field--checks">
                  <label className="bbp-check-row">
                    <input
                      type="checkbox"
                      checked={editItem.in_stock}
                      onChange={(e) => setEditItem({ ...editItem, in_stock: e.target.checked })}
                    />
                    <span>In stock (shown to customers)</span>
                  </label>
                  <label className="bbp-check-row">
                    <input
                      type="checkbox"
                      checked={editItem.active}
                      onChange={(e) => setEditItem({ ...editItem, active: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </>
            )}
            <div className="bbp-actions">
              <button type="button" className="bbp-btn bbp-btn-primary" onClick={() => void saveItem()}>
                Save
              </button>
              <button type="button" className="bbp-btn" onClick={() => setEditItem(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
